<template>
  <div class="breakout-widget" :class="{ 'breakout-widget--phone': phoneRooms }">
    <!-- A phone (prototype Mobile Room, sheetRooms, host): set up in three
         rows and Open; while open, the clock, each room with Join, and Close
         all rooms. -->
    <div v-if="phoneRooms" class="bo-phone" data-testid="rooms-host">
      <template v-if="!sessionStore.breakoutsActive">
        <p class="bo-phone__note">Split everyone into small groups, then bring them back together.</p>
        <div class="bo-phone__row">
          <div>Rooms<span data-testid="rooms-about">{{ aboutEach(sessionParticipantsList.length, roomsCount) }}</span></div>
          <div class="bo-phone__step">
            <button type="button" aria-label="Fewer rooms" :disabled="roomsCount <= 1" data-testid="rooms-fewer" @click="stepRooms(-1)">−</button>
            <b data-testid="rooms-count">{{ roomsCount }}</b>
            <button type="button" aria-label="More rooms" :disabled="roomsCount >= MAX_ROOMS" data-testid="rooms-more" @click="stepRooms(1)">+</button>
          </div>
        </div>
        <div class="bo-phone__row bo-phone__row--block">
          <div>Assign people</div>
          <div class="bo-phone__seg" role="radiogroup" aria-label="Assign people">
            <button type="button" role="radio" :aria-checked="String(phoneMode === 'auto')" :class="{ on: phoneMode === 'auto' }" data-testid="rooms-auto" @click="phoneMode = 'auto'">Automatically</button>
            <button type="button" role="radio" :aria-checked="String(phoneMode === 'choose')" :class="{ on: phoneMode === 'choose' }" data-testid="rooms-choose" @click="phoneMode = 'choose'">Let them choose</button>
          </div>
        </div>
        <div class="bo-phone__row">
          <div>Close after<span>A 60-second warning comes first</span></div>
          <div class="bo-phone__step">
            <button type="button" aria-label="Five minutes less" :disabled="phoneMinutes <= 5" data-testid="rooms-shorter" @click="stepMinutes(-5)">−</button>
            <b data-testid="rooms-minutes">{{ phoneMinutes }}m</b>
            <button type="button" aria-label="Five minutes more" :disabled="phoneMinutes >= 60" data-testid="rooms-longer" @click="stepMinutes(5)">+</button>
          </div>
        </div>
        <button type="button" class="bo-phone__btn" :disabled="isStarting" data-testid="rooms-open" @click="openPhoneRooms">
          <BoIcon name="users" :size="16"/>
          {{ isStarting ? 'Opening…' : `Open ${roomsCount} rooms` }}
        </button>
      </template>

      <template v-else>
        <div class="bo-phone__live">
          <b>{{ phoneClosing ? 'Closing' : 'Rooms are open' }}</b>
          <p v-if="phoneSeconds !== null">{{ phoneClosing ? 'Everyone returns in ' : 'Close automatically in ' }}<span class="bo-phone__clock" data-testid="rooms-clock">{{ breakoutClock(phoneSeconds) }}</span></p>
          <p v-else>Open until you close them.</p>
          <button v-if="sessionStore.isInBreakout" type="button" class="bo-phone__btn bo-phone__btn--g" data-testid="rooms-host-return" @click="sessionStore.requestReturnToMain()">Return to main session</button>
        </div>
        <div v-for="(room, i) in sessionStore.breakoutRooms" :key="room.roomName" class="bo-phone__card" data-testid="rooms-host-room">
          <div class="bo-phone__t">
            <b>{{ room.displayName || `Room ${i + 1}` }}</b>
            <span>{{ (room.participantIdentities || []).length }} {{ (room.participantIdentities || []).length === 1 ? 'person' : 'people' }}</span>
            <div class="bo-phone__avs" aria-hidden="true">
              <span v-for="id in (room.participantIdentities || []).slice(0, 5)" :key="id">{{ initialsOf(getParticipantName(id)) }}</span>
            </div>
          </div>
          <button
              type="button"
              class="bo-phone__join"
              :disabled="sessionStore.breakoutRoomName === room.roomName"
              :aria-label="`Join ${room.displayName || `Room ${i + 1}`}`"
              data-testid="rooms-host-join"
              @click="joinRoom(room.roomName)"
          >{{ sessionStore.breakoutRoomName === room.roomName ? 'Here' : 'Join' }}</button>
        </div>
        <button v-if="!phoneClosing" type="button" class="bo-phone__btn bo-phone__btn--red" data-testid="rooms-close-all" @click="closeAllPhone">Close all rooms</button>
      </template>
    </div>

    <template v-else>
    <Tooltip
      v-if="breakoutTooltip.visible"
      :text="breakoutTooltip.text"
      :initial="breakoutTooltip.initial"
      :x-position="breakoutTooltip.xPosition"
      :target-left="breakoutTooltip.targetLeft"
      :rect="breakoutTooltip.target?.getBoundingClientRect()"
      @close="handleBreakoutTooltipClose"
    />
    <!--
      Rooms is a section of the panel now: the panel's header names it and
      holds the one hide control (prototype .rmhd3 — "two ways to hide the
      same panel, eight pixels apart, is worse than one"). What stays is the
      way back out of setting rooms up, which only this widget knows.
    -->
    <div v-if="view === 'setup'" class="widget-header">
      <button type="button" class="icon-btn" :aria-label="isEditMode ? 'Cancel editing' : 'Back'" @click="handleBack">
        <BoIcon name="chevl" :size="16"/>
      </button>
    </div>

    <!-- Content -->
    <div class="widget-content">

      <!-- View: Start (Empty State) -->
      <div v-if="view === 'start' && !sessionStore.breakoutsActive && !sessionStore.breakoutsEnded" class="empty-state">
        <span class="empty-icon-wrapper">
          <IconBreakoutRoom id="start-icon-breakout-room" size="22" color="currentColor"/>
        </span>
        <h3 class="empty-title">Breakout Sessions</h3>
        <p class="empty-desc">
          Use the dialog to configure and launch breakout rooms.
        </p>
        <XlButton size="sm" @click="initializeSetup">
          <template #icon><BoIcon name="plus" :size="14"/></template>
          Set Up Breakouts
        </XlButton>
      </div>

      <!-- View: Ended State -->
      <div v-else-if="view === 'start' && !sessionStore.breakoutsActive && sessionStore.breakoutsEnded" class="empty-state ended-state">
        <span class="empty-icon-wrapper check-icon">
          <BoIcon name="check" :size="22"/>
        </span>
        <h3 class="empty-title">Breakouts Ended</h3>
        <p class="empty-desc">
          All participants have returned to the main room.
        </p>
        <XlButton size="sm" @click="initializeSetup">
          <template #icon><BoIcon name="repeat" :size="14"/></template>
          Restart Breakouts
        </XlButton>
      </div>

      <!-- View: Breakouts Active -->
      <div v-else-if="view === 'start' && sessionStore.breakoutsActive" class="active-breakouts-container">
        <!-- Stats Row -->
        <div class="active-stats-row">
          <div class="stats-left">
            <span class="stat-room-name">Main Room</span>
          </div>
          <div class="stats-right">
            <div class="stat-item"
                 @mouseenter="(e) => handleBreakoutTooltipEnter(e, 'Number of breakout rooms currently active')"
                 @mouseleave="handleBreakoutTooltipLeave"
            >
              <IconBreakoutRoom id="active-icon-breakout-room" size="14" color="currentColor"/>
              <span>{{ sessionStore.breakoutRooms.length }}</span>
            </div>
            <div class="stat-item"
                 @mouseenter="(e) => handleBreakoutTooltipEnter(e, 'Total participants currently assigned to breakout rooms')"
                 @mouseleave="handleBreakoutTooltipLeave"
            >
              <BoIcon name="users" :size="14"/>
              <span>{{ totalBreakoutParticipants }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-live-indicator">
              <span class="live-dot"></span> Live
            </div>
            <div v-if="sessionStore.breakoutEndTime" class="stat-time"
                 @mouseenter="(e) => handleBreakoutTooltipEnter(e, 'Time remaining before breakout rooms close', 'top', 'right')"
                 @mouseleave="handleBreakoutTooltipLeave"
            >
              {{ formattedTimeRemaining }}
            </div>
          </div>
        </div>

        <!-- Action Row -->
        <div class="active-action-row">
          <XlButton variant="ghost" size="sm" @click="initializeEditMode">
            <template #icon><BoIcon name="pen" :size="13"/></template>
            Edit Breakout
          </XlButton>
          <XlButton variant="ghost-danger" size="sm" @click="openEndBreakoutModal">
            Close all rooms
          </XlButton>
        </div>

        <!-- Room List -->
        <div class="rooms-list active-view-list">
          <!-- Main Room Entry -->
          <div class="room-card main-room-active" :class="{ expanded: mainRoomExpanded }">
             <div class="room-header" @click="mainRoomExpanded = !mainRoomExpanded">
               <div class="room-header-left">
                 <span class="toggle-icon" :title="mainRoomExpanded ? 'Collapse' : 'Open'">
                    <BoIcon name="chevd" :size="14"/>
                 </span>
                 <span class="room-name">Main Session</span>
                 <span class="room-count">{{ mainSessionParticipants.length }}</span>
               </div>
               <div class="room-actions">
                 <XlButton
                   v-if="sessionStore.isInBreakout"
                   class="room-join"
                   variant="ghost"
                   size="sm"
                   @click.stop="joinRoom(sessionStore.mainRoomNameForReturn || sessionStore.roomName)"
                 >Return</XlButton>
                 <XlTag v-else tone="brand" class="current-room-badge">You are here</XlTag>
               </div>
             </div>
             <div v-if="mainRoomExpanded" class="room-content">
                <div class="participants-chips">
                   <div v-for="p in mainSessionParticipants" :key="p.participantIdentity" class="item-container__selected__item is-static">
                      <span class="item-container__selected__item__img">
                        <IconDefaultAvatar :size="20" />
                      </span>
                      <span class="item-container__selected__item__title">{{ p.participantName.replace(' (you)', '') }}</span>
                   </div>
                </div>
                <div v-if="mainSessionParticipants.length === 0" class="room-empty-state">
                  No participants
                </div>
             </div>
          </div>

          <!-- Breakout Rooms -->
          <div v-for="(room, index) in sessionStore.breakoutRooms" :key="index" class="room-card" :class="{ expanded: room.expanded }">
             <div class="room-header" @click="room.expanded = !room.expanded">
               <div class="room-header-left">
                 <span class="toggle-icon" :title="room.expanded ? 'Collapse' : 'Open'">
                    <BoIcon name="chevd" :size="14"/>
                 </span>
                 <span class="room-name">{{ room.displayName || `Room ${index + 1}` }}</span>
                 <span class="room-count">{{ room.participantIdentities.length }}</span>
               </div>
               <div class="room-actions">
                 <XlButton
                   v-if="sessionStore.currentRoomName !== room.roomName"
                   class="room-join"
                   variant="ghost"
                   size="sm"
                   @click.stop="joinRoom(room.roomName)"
                 >Join</XlButton>
                 <XlTag v-else tone="brand" class="current-room-badge">You are here</XlTag>
               </div>
             </div>
             <div v-if="room.expanded" class="room-content">
                <div class="participants-chips">
                   <div v-for="identity in room.participantIdentities" :key="identity" class="item-container__selected__item is-static">
                      <span class="item-container__selected__item__img">
                        <IconDefaultAvatar :size="20" />
                      </span>
                      <span class="item-container__selected__item__title">{{ getParticipantName(identity) }}</span>
                   </div>
                </div>
                <div v-if="room.participantIdentities.length === 0" class="room-empty-state">
                  No participants
                </div>
             </div>
          </div>
        </div>

      </div>

      <!-- View: Setup -->
      <div v-else-if="view === 'setup'" class="setup-container">

        <!-- Top Controls (.botop) -->
        <div class="controls-row">
          <div class="control-group">
            <label :for="roomsInputId">Rooms:</label>
            <div
                class="number-input-wrapper"
                :class="{ focused: roomsInputFocused }"
                @mouseenter="(e) => handleBreakoutTooltipEnter(e, 'Set total breakout rooms', 'bottom', 'center')"
                @mouseleave="handleBreakoutTooltipLeave"
            >
              <button
                  type="button"
                  aria-label="One fewer room"
                  @click="roomsCount > 1 && roomsCount--"
                  class="spin-btn down"
              >−</button>
              <input
                  :id="roomsInputId"
                  type="number"
                  v-model="roomsCount"
                  min="1"
                  :max="MAX_ROOMS"
                  class="rooms-input"
                  @focus="roomsInputFocused = true"
                  @blur="roomsInputFocused = false"
              />
              <button
                  type="button"
                  aria-label="One more room"
                  @click="roomsCount < MAX_ROOMS && roomsCount++"
                  :disabled="roomsCount >= MAX_ROOMS"
                  class="spin-btn up"
              >+</button>
            </div>
          </div>

          <div class="control-actions">
            <XlTooltip v-if="!settings.attendeesChoose" control text="Randomly reassign participants to rooms">
              <XlButton
                class="shuffle-btn"
                variant="ghost"
                size="sm"
                :disabled="rooms.length <= 1"
                @click="shuffleParticipants({ includeMain: false })"
              >
                <template #icon><BoIcon name="repeat" :size="13"/></template>
                Shuffle
              </XlButton>
            </XlTooltip>

            <XlTooltip v-if="!settings.attendeesChoose" control text="Remove all room assignments">
              <button type="button" class="lnk" @click="clearAssignments">Clear All</button>
            </XlTooltip>
          </div>
        </div>

        <!-- Mode Toggle & Settings (.boseg) -->
        <div class="mode-row">
          <XlTooltip
            class="mode-toggle"
            control
            :text="isAutomatic ? 'Automatic - evenly distributed' : 'Manual - drag & drop'"
            :style="{ opacity: settings.attendeesChoose ? 0.5 : 1, pointerEvents: settings.attendeesChoose ? 'none' : 'auto' }"
          >
            <div class="seg" role="group" aria-label="How people are placed in rooms">
              <button type="button" :class="{ on: isAutomatic }" :aria-pressed="isAutomatic ? 'true' : 'false'" @click="isAutomatic = true">Automatic</button>
              <button type="button" :class="{ on: !isAutomatic }" :aria-pressed="!isAutomatic ? 'true' : 'false'" @click="isAutomatic = false">Manual</button>
            </div>
          </XlTooltip>

          <div class="settings-wrapper">
            <XlTooltip control text="Breakout Settings">
              <button
                  type="button"
                  class="ib"
                  :class="{ on: showSettings }"
                  aria-label="Breakout Settings"
                  :aria-expanded="showSettings ? 'true' : 'false'"
                  @click="toggleSettingsMenu"
              >
                <BoIcon name="gear" :size="15"/>
              </button>
            </XlTooltip>

            <!-- Settings (.boset) -->
            <div v-if="showSettings" class="settings-dropdown" ref="settingsDropdown" @click.stop>
              <div class="settings-header">
                <div class="settings-header-text">
                  <h4>Breakout Settings</h4>
                  <div class="settings-subtitle">Set permissions and defaults for breakout rooms</div>
                </div>
                <button type="button" @click="showSettings = false" class="close-settings" aria-label="Close settings">×</button>
              </div>
              <div class="settings-body">
<!--                <div class="setting-item">-->
<!--                  <pge-switch v-model="settings.hostManagement" label="Host management" size="xs" />-->
<!--                </div>-->
                <div class="setting-item">
                  <XlSwitch v-model="settings.setTimeLimit" label="Set time Limit" />
                </div>
                <div class="setting-item select-item" :class="{ disabled: !settings.setTimeLimit }">
                  <span class="setting-label">Breakout Duration</span>
                  <div class="select-wrapper">
                    <input type="number" class="config-input" v-model.number="settings.breakoutDuration" :disabled="!settings.setTimeLimit" min="1" aria-label="Breakout Duration" />
                    <span class="input-suffix">min</span>
                  </div>
                </div>
                <div class="setting-item">
                  <XlSwitch v-model="settings.showCountdown" label="Show countdown before rooms close" />
                </div>
                <div class="setting-item select-item" :class="{ disabled: !settings.showCountdown }">
                  <span class="setting-label">Closing countdown</span>
                  <div class="select-wrapper">
                    <input type="number" class="config-input" v-model.number="settings.closingCountdown" :disabled="!settings.showCountdown" min="1" max="60" aria-label="Closing countdown" />
                    <span class="input-suffix">sec</span>
                  </div>
                </div>
                <div class="setting-item">
                  <XlSwitch v-model="settings.allowLeaveEarly" label="Allow participants to leave early" />
                </div>
                <div class="setting-item">
                  <XlSwitch v-model="settings.attendeesChoose" label="Allow participants to choose breakout rooms" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Room List (.borooms-list) -->
        <div class="rooms-list">
          <!-- Main Room -->
          <div class="room-card main-room" :class="{ expanded: mainRoomExpanded }">
            <div class="room-header" @click="mainRoomExpanded = !mainRoomExpanded">
              <div class="room-header-left">
                <span class="toggle-icon" :title="mainRoomExpanded ? 'Collapse' : 'Open'">
                  <BoIcon name="chevd" :size="14"/>
                </span>
                <span class="room-name">Main Room</span>
                <span class="room-count">{{ mainList.length }}</span>
              </div>

            </div>

            <div v-if="mainRoomExpanded" class="room-content">
              <draggable
                  v-model="mainList"
                  group="participants"
                  item-key="name"
                  class="participants-chips"
                  ghost-class="ghost-card"
                  :disabled="isAutomatic || settings.attendeesChoose"
              >
                <template #item="{element, index}">
                  <div class="item-container__selected__item" :class="{ 'is-static': isAutomatic || settings.attendeesChoose }">
                        <span class="item-container__selected__item__img">
                          <img v-if="element.avatar" :src="element.avatar" :alt="element.name"/>
                          <IconDefaultAvatar v-else :size="20" />
                        </span>
                    <span class="item-container__selected__item__title">{{ element.name }}</span>
                    <button
                        v-if="!isAutomatic && !settings.attendeesChoose"
                        type="button"
                        class="icon-close"
                        :aria-label="`Remove ${element.name}`"
                        @click.stop="removeParticipantMain(index)"
                    >
                      <BoIcon name="x" :size="11"/>
                    </button>
                  </div>
                </template>
              </draggable>
              <div v-if="mainList.length === 0" class="room-empty-state">
                <span v-if="!settings.attendeesChoose" class="drop-hint">Drag participants here</span>
              </div>
            </div>
          </div>

          <!-- Generated Rooms -->
          <div v-for="(room, index) in rooms" :key="index" class="room-card" :class="{ expanded: room.expanded, 'menu-open': activeMenuRoomIndex === index }">
            <div class="room-header" @click="room.expanded = !room.expanded">
              <div class="room-header-left">
                <span class="toggle-icon" :title="room.expanded ? 'Collapse' : 'Open'">
                  <BoIcon name="chevd" :size="14"/>
                </span>
                <div v-if="renamingRoomIndex === index" class="rename-input-wrapper" @click.stop>
                  <input
                      v-model="newRoomName"
                      aria-label="Room name"
                      @keyup.enter="saveRoomName(index)"
                      @blur="saveRoomName(index)"
                      ref="renameInput"
                      class="rename-input"
                  />
                </div>
                <span v-else class="room-name">{{ room.name || `Room ${index + 1}` }}</span>
                <span class="room-count">{{ room.participants.length }}</span>
              </div>
              <div class="room-menu-wrapper">
                <button
                    type="button"
                    class="icon-btn dots-btn"
                    aria-label="Room options"
                    :aria-expanded="activeMenuRoomIndex === index ? 'true' : 'false'"
                    @click="toggleRoomMenu(index, $event)"
                >⋯</button>
                <div v-if="activeMenuRoomIndex === index" class="menu-dropdown">
                  <button type="button" class="menu-item" @click.stop="startRenameRoom(index)">
                    <span class="menu-icon">
                      <BoIcon name="pen" :size="13"/>
                    </span>
                    <span>Rename</span>
                  </button>
                  <button v-if="room.participants.length > 0" type="button" class="menu-item" @click.stop="confirmClearRoom(index)">
                    <span class="menu-icon">
                      <BoIcon name="users" :size="13"/>
                    </span>
                    <span>Clear Room</span>
                  </button>
                  <button type="button" class="menu-item delete" @click.stop="confirmDeleteRoom(index)">
                    <span class="menu-icon">
                      <BoIcon name="x" :size="13"/>
                    </span>
                    <span>Delete Room</span>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="room.expanded" class="room-content">
              <draggable
                  v-model="room.participants"
                  group="participants"
                  item-key="name"
                  class="participants-chips"
                  ghost-class="ghost-card"
                  :disabled="isAutomatic || settings.attendeesChoose"
              >
                <template #item="{element, index: pIdx}">
                  <div class="item-container__selected__item" :class="{ 'is-static': isAutomatic || settings.attendeesChoose }">
                       <span class="item-container__selected__item__img">
                         <img v-if="element.avatar" :src="element.avatar" :alt="element.name"/>
                         <IconDefaultAvatar v-else :size="20" />
                       </span>
                    <span class="item-container__selected__item__title">{{ element.name }}</span>
                    <button
                        v-if="!isAutomatic && !settings.attendeesChoose"
                        type="button"
                        class="icon-close"
                        :aria-label="`Move ${element.name} back to the main room`"
                        title="Back to the main room"
                        @click.stop="removeParticipant(index, pIdx)"
                    >
                      <BoIcon name="x" :size="11"/>
                    </button>
                  </div>
                </template>
              </draggable>
              <div v-if="room.participants.length === 0" class="room-empty-state">
                <span v-if="!settings.attendeesChoose" class="drop-hint">Drag and drop participants here</span>
              </div>
            </div>
          </div>

          <XlButton
              class="add-room"
              variant="ghost"
              size="sm"
              :disabled="rooms.length >= MAX_ROOMS"
              @click="addRoom"
          >
            <template #icon><BoIcon name="plus" :size="14"/></template>
            Add Room
          </XlButton>
        </div>

        <!-- Start Button Wrapper -->
      </div>
    </div>

    <!-- Footer for Active View (Broadcast) -->
    <div class="breakout-footer" v-if="view === 'start' && sessionStore.breakoutsActive">
      <div class="footer-actions">
        <XlButton size="sm" @click="showBroadcastModal = true">
          <template #icon><BoIcon name="mail" :size="14"/></template>
          Broadcast
        </XlButton>
      </div>
    </div>

    <!-- Footer for Setup View -->
    <div class="breakout-footer" v-if="view === 'setup'">
      <div v-if="validationError" class="validation-error" role="alert">
        {{ validationError }}
      </div>
      <div v-if="isEditMode" class="footer-actions footer-actions--edit">
        <XlButton variant="ghost" size="sm" @click="cancelEdit">Cancel</XlButton>
        <XlButton
            size="sm"
            :disabled="isSaving || !!validationError"
            @click="saveBreakoutEdits"
        >{{ isSaving ? 'Saving...' : 'Save' }}</XlButton>
      </div>
      <div v-else class="footer-actions">
        <XlButton
            size="sm"
            :disabled="isStarting || !!validationError"
            @click="startBreakouts"
        >
          <template #icon><BoIcon :name="sessionStore.breakoutsActive ? 'check' : 'users'" :size="14"/></template>
          {{ isStarting ? 'Saving...' : (sessionStore.breakoutsActive ? 'Save' : 'Start Breakouts') }}
        </XlButton>
      </div>
    </div>

    <!-- Modals -->
    <SessionDialog v-if="showClearModal" title="Clear Room" icon="door" dismissible backdrop-closes data-testid="breakout-clear-dialog" @close="closeClearModal">
      <p>Participants will be moved back to the main session immediately</p>
      <template #actions>
        <XlButton variant="ghost" @click="closeClearModal">Cancel</XlButton>
        <XlButton @click="executeClearRoom">Clear room</XlButton>
      </template>
    </SessionDialog>

    <SessionDialog v-if="showDeleteModal" title="Delete Room" tone="cr" icon="trash" dismissible backdrop-closes data-testid="breakout-delete-dialog" @close="closeDeleteModal">
      <p>Participants will be moved back to the main session immediately</p>
      <template #actions>
        <XlButton variant="ghost" @click="closeDeleteModal">Cancel</XlButton>
        <XlButton variant="danger" @click="executeDeleteRoom">Delete room</XlButton>
      </template>
    </SessionDialog>

    <!-- Broadcast Modal (.mod / .mdl.ses) -->
    <div v-if="showBroadcastModal" class="modal-overlay">
      <div class="modal-card" role="dialog" aria-modal="true" :aria-labelledby="broadcastTitleId">
        <div class="modal-head">
          <span class="modal-mark" aria-hidden="true"><BoIcon name="mail" :size="22"/></span>
          <h3 :id="broadcastTitleId">Broadcast to all rooms</h3>
        </div>
        <div class="modal-body">
          <textarea
              v-model="broadcastText"
              placeholder="Type your message..."
              aria-label="Broadcast message"
              rows="4"
              :maxlength="100"
          ></textarea>

          <div class="modal-footer">
            <div class="char-count">{{ broadcastText.length }} / 100</div>
          </div>
        </div>

        <div class="modal-actions">
          <XlButton variant="ghost" @click="showBroadcastModal = false">Cancel</XlButton>
          <XlButton
              :disabled="!broadcastText.trim()"
              @click="submitBroadcast"
          >Submit</XlButton>
        </div>
      </div>
    </div>

    <!-- End Breakout Modal -->
    <SessionDialog v-if="showEndBreakoutModal" title="Close all breakout rooms?" icon="door" backdrop-closes data-testid="breakout-end-dialog" @close="showEndBreakoutModal = false">
      <p v-if="settings.showCountdown">Everyone will be returned to the main room in {{ settings.closingCountdown || 30 }} seconds.</p>
      <p v-else>Everyone will be returned to the main room immediately.</p>
      <template #actions>
        <XlButton variant="ghost" @click="showEndBreakoutModal = false">Cancel</XlButton>
        <XlButton @click="confirmEndBreakouts">Close all rooms</XlButton>
      </template>
    </SessionDialog>
    </template>
  </div>
</template>

<script setup>
import { breakoutRoomName as nextBreakoutRoom } from '../../helpers/breakoutRooms';
import { aboutEach, breakoutClock } from '../../helpers/breakoutPhone';
import {ref, reactive, onMounted, onUnmounted, computed, watch, nextTick, useId, h} from 'vue';
import axios from 'axios';
import {useSessionStore} from '../../stores/sessionStore';
import Tooltip from '../../pge-components/pge-tooltip.vue';
import SessionDialog from '../../livekit-client/components/SessionDialog.vue';
import XlButton from '../xl/XlButton.vue';
import XlSwitch from '../xl/XlSwitch.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import IconBreakoutRoom from '../icons/IconBreakoutRoom.vue';
import IconDefaultAvatar from '../icons/IconDefaultAvatar.vue';
import draggable from 'vuedraggable';

// The prototype's stroke icons (J set, 24×24), drawn in the current colour.
// Smaller icons get a heavier line, as the prototype compensates optically.
const BO_ICONS = {
  plus: ['M12 6.2v11.6M6.2 12h11.6'],
  x: ['m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8'],
  check: ['m5.8 12.3 4.2 4.2L18.2 7.8'],
  chevd: ['m6 9.8 6 6 6-6'],
  chevl: ['m14.2 18-6-6 6-6'],
  chevrr: ['m6.4 17.6 5.6-5.6-5.6-5.6', 'm12.6 17.6 5.6-5.6-5.6-5.6'],
  repeat: ['M5 11V9a2.5 2.5 0 0 1 2.5-2.5h11', 'm15.6 3.6 3 3-3 3', 'M19 13v2a2.5 2.5 0 0 1-2.5 2.5h-11', 'm8.4 20.4-3-3 3-3'],
  gear: ['M4.4 7.4h11M18.6 7.4h1M4.4 16.6h1M8.6 16.6h11', { circle: [17, 7.4, 2.4] }, { circle: [7, 16.6, 2.4] }],
  users: [{ circle: [9, 8, 3.2] }, 'M3.6 20v-1.4A4 4 0 0 1 7.6 14.6h2.8a4 4 0 0 1 4 4V20', { circle: [17.4, 9, 2.4] }, 'M16 14.7h1.8a3 3 0 0 1 3 3V20'],
  mail: [{ rect: true }, 'm4.6 8 7.4 5 7.4-5'],
  pen: ['M14.6 5.6 18.4 9.4', 'M5 19l.9-4L15.6 5.3a1.6 1.6 0 0 1 2.3 0l.8.8a1.6 1.6 0 0 1 0 2.3L9 18.1z'],
};
const BoIcon = (props) => {
  const size = Number(props.size) || 16;
  const children = (BO_ICONS[props.name] ?? []).map((shape) => {
    if (typeof shape === 'string') return h('path', { d: shape });
    if (shape.circle) return h('circle', { cx: shape.circle[0], cy: shape.circle[1], r: shape.circle[2] });
    return h('rect', { x: 3.5, y: 5.5, width: 17, height: 13, rx: 2.5 });
  });
  return h('svg', {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': size < 13 ? 2.1 : size < 17 ? 1.9 : 1.7,
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
    'aria-hidden': 'true',
  }, children);
};
BoIcon.props = ['name', 'size'];

const emit = defineEmits(['back', 'close']);

const sessionStore = useSessionStore();

const MAX_ROOMS = 10;

// State
const view = ref('start'); // 'start' | 'setup'
const roomsCount = ref(2);
const isAutomatic = ref(true);
const showSettings = ref(false);
const roomsInputFocused = ref(false);
const roomsInputId = useId();
const broadcastTitleId = useId();
const activeMenuRoomIndex = ref(null);
const renamingRoomIndex = ref(null);
const newRoomName = ref('');
const renameInput = ref([]);
const isStarting = ref(false);
const isEditMode = ref(false);
const isSaving = ref(false);
const originalBreakoutDuration = ref(null);

// Broadcast & End Breakout state
const showBroadcastModal = ref(false);
const broadcastText = ref('');
const showEndBreakoutModal = ref(false);

// Modal state
const showClearModal = ref(false);
const showDeleteModal = ref(false);
const roomToClear = ref(null);
const roomToDelete = ref(null);

// Tooltip refs
const tooltips = reactive({
  shuffle: false,
  clear: false,
  mode: false,
  settings: false
});

const breakoutTooltip = reactive({
  visible: false,
  target: null,
  text: '',
  targetLeft: false,
  initial: 'top',
  xPosition: 'center'
});

const handleBreakoutTooltipEnter = async (e, text, initial = 'top', xPosition = 'center') => {
  if (breakoutTooltip.target !== e.target) {
    breakoutTooltip.visible = false;
  }
  await nextTick();
  breakoutTooltip.target = e.target;
  breakoutTooltip.text = text;
  breakoutTooltip.initial = initial;
  breakoutTooltip.xPosition = xPosition;
  breakoutTooltip.targetLeft = false;
  breakoutTooltip.visible = true;
};

const handleBreakoutTooltipLeave = () => {
  breakoutTooltip.targetLeft = true;
};

const handleBreakoutTooltipClose = () => {
  breakoutTooltip.visible = false;
  breakoutTooltip.targetLeft = false;
  breakoutTooltip.target = null;
  breakoutTooltip.text = '';
};

const settings = reactive({
  hostManagement: true,
  setTimeLimit: false,
  breakoutDuration: 15,
  showCountdown: false,
  closingCountdown: 30,
  allowLeaveEarly: true,
  attendeesChoose: false
});

// Room data
const mainRoomExpanded = ref(true);

const mainSessionParticipants = computed(() => {
  const identitiesInBreakouts = new Set(
    sessionStore.breakoutRooms.flatMap(r => r.participantIdentities || [])
  );

  if (sessionStore.breakoutsActive) {
    const result = [];
    const seen = new Set();

    // 1. Participants currently in admin's LiveKit room (most up-to-date data)
    sessionStore.sessionParticipants.forEach(p => {
      if (!p.isScreenShareTile && !identitiesInBreakouts.has(p.participantIdentity)) {
        seen.add(p.participantIdentity);
        result.push(p);
      }
    });

    // 2. Participants explicitly marked as in-transit from a breakout back to main.
    //    Driven by markReturningFromBreakout (server-confirmed departures from a
    //    breakout room) — not by stale entries in participantNames, which would
    //    otherwise surface attendees who left the session entirely.
    for (const identity of sessionStore.returningFromBreakout) {
      if (!seen.has(identity) &&
          !identitiesInBreakouts.has(identity) &&
          identity !== sessionStore.localParticipantIdentity) {
        const name = sessionStore.participantNames[identity];
        if (name) {
          seen.add(identity);
          result.push({
            participantIdentity: identity,
            participantName: name
          });
        }
      }
    }

    return result;
  }

  // When breakouts are not active, use LiveKit-based participants only
  return sessionStore.sessionParticipants.filter(p =>
    !p.isScreenShareTile &&
    !identitiesInBreakouts.has(p.participantIdentity)
  );
});

const totalBreakoutParticipants = computed(() => {
  return sessionStore.breakoutRooms.reduce((acc, room) => acc + (room.participantIdentities?.length || 0), 0);
});

const mainList = ref([]);
const rooms = ref([]);

// Build participant list from session store (excluding local/admin participant)
const sessionParticipantsList = computed(() => {
  return sessionStore.sessionParticipants
    .filter(p => {
      // Exclude the local participant (admin) from breakout assignment
      if (p.participantIdentity === sessionStore.localParticipantIdentity) return false;
      // Exclude screen share tiles
      if (p.isScreenShareTile) return false;
      return true;
    })
    .map(p => ({
      name: p.participantName?.replace(' (you)', '') || 'Unknown',
      identity: p.participantIdentity,
      avatar: null // Avatar comes from participant data if available
    }));
});

// Validation rules for inputs
const validationError = computed(() => {
  if (roomsCount.value > MAX_ROOMS) return `Max ${MAX_ROOMS} rooms allowed.`;
  if (settings.setTimeLimit) {
    if (!settings.breakoutDuration || settings.breakoutDuration <= 0) return 'Duration must be > 0 min.';
    if (settings.showCountdown) {
      if (!settings.closingCountdown || settings.closingCountdown <= 0) return 'Countdown must be > 0 sec.';
      if (settings.closingCountdown > 60) return 'Max countdown is 60 sec.';
      if (settings.closingCountdown >= settings.breakoutDuration * 60) return 'Countdown must be < duration.';
    }
  } else if (settings.showCountdown) {
     if (!settings.closingCountdown || settings.closingCountdown <= 0) return 'Countdown must be > 0 sec.';
     if (settings.closingCountdown > 60) return 'Max countdown is 60 sec.';
  }
  return null;
});

// Watch for real-time participant changes during setup
watch(() => sessionParticipantsList.value, (newParticipants) => {
  if (view.value === 'setup') {
    let newGuyAdded = false;
    // Add new participants that aren't in mainList or any room.participants
    newParticipants.forEach(p => {
      const inMain = mainList.value.some(m => m.identity === p.identity);
      const inRooms = rooms.value.some(r => r.participants.some(rp => rp.identity === p.identity));
      if (!inMain && !inRooms) {
        mainList.value.push(p);
        newGuyAdded = true;
      }
    });

    // Remove participants that left
    mainList.value = mainList.value.filter(m => newParticipants.some(np => np.identity === m.identity));
    rooms.value.forEach(r => {
      r.participants = r.participants.filter(rp => newParticipants.some(np => np.identity === rp.identity));
    });

    if (newGuyAdded && isAutomatic.value && !settings.attendeesChoose && rooms.value.length > 0) {
      // Find the rooms with the least participants and add them there
      let newGuys = [...mainList.value];
      mainList.value = [];
      const newRooms = [...rooms.value];

      newGuys.forEach(participant => {
         let minRoom = newRooms[0];
         for(let i = 1; i < newRooms.length; i++) {
             if(newRooms[i].participants.length < minRoom.participants.length) {
                 minRoom = newRooms[i];
             }
         }
         minRoom.participants.push(participant);
      });
      rooms.value = newRooms;
    }
  }
}, { deep: true });

// Initialize rooms when entering setup view
const initializeSetup = () => {
  view.value = 'setup';
  // Clear any stale room assignments from a previous breakout session
  rooms.value = [];
  // Populate mainList with all session participants
  mainList.value = [...sessionParticipantsList.value];
  // Generate empty rooms based on roomsCount
  generateRooms();

  if (isAutomatic.value && !settings.attendeesChoose) {
    shuffleParticipants({ includeMain: true });
  }
};

// Generate rooms array based on roomsCount
const generateRooms = () => {
  const currentCount = rooms.value.length;
  const targetCount = Math.min(roomsCount.value, MAX_ROOMS);

  if (targetCount > currentCount) {
    // Add new rooms
    for (let i = currentCount; i < targetCount; i++) {
      rooms.value.push({
        id: i + 1,
        expanded: true,
        participants: []
      });
    }
  } else if (targetCount < currentCount) {
    // Remove extra rooms, move their participants back to main
    const removed = rooms.value.splice(targetCount);
    removed.forEach(r => {
      mainList.value.push(...r.participants);
    });
  }
};

// Watch roomsCount changes to adjust rooms array
watch(roomsCount, () => {
  if (view.value === 'setup') {
    generateRooms();
    if (isAutomatic.value && !settings.attendeesChoose) {
      shuffleParticipants({ includeMain: true });
    }
  }
});

// Watch isAutomatic changes to adjust room distribution
watch(isAutomatic, (newVal) => {
  if (view.value === 'setup' && newVal === true && !settings.attendeesChoose) {
    shuffleParticipants({ includeMain: true });
  }
});

watch(() => settings.attendeesChoose, (newVal) => {
  // In edit mode, preserve existing room assignments so attendees who
  // already chose a room are not redirected back to the main room.
  // Only attendees not yet assigned will see the choose-room snackbar.
  if (view.value === 'setup' && !isEditMode.value) {
    if (newVal) {
      clearAssignments();
    } else if (isAutomatic.value) {
      shuffleParticipants({ includeMain: true });
    }
  }
});

const handleBack = () => {
  if (view.value === 'setup') {
    if (isEditMode.value) {
      cancelEdit();
    } else {
      view.value = 'start';
    }
  } else {
    emit('back');
  }
};

const toggleSettingsMenu = () => {
  showSettings.value = !showSettings.value;
};

const handleClickOutside = (event) => {
  // Settings check — close if click is outside the entire settings wrapper
  if (showSettings.value && !event.target.closest('.settings-wrapper')) {
    showSettings.value = false;
  }

  // Room menu check
  if (activeMenuRoomIndex.value !== null && !event.target.closest('.room-menu-wrapper')) {
    activeMenuRoomIndex.value = null;
  }
};

const startRenameRoom = (index) => {
  renamingRoomIndex.value = index;
  newRoomName.value = rooms.value[index].name || `Room ${index + 1}`;
  activeMenuRoomIndex.value = null;
};

const saveRoomName = (index) => {
  if (renamingRoomIndex.value !== index) return;
  if (index >= 0 && index < rooms.value.length) {
    rooms.value[index].name = newRoomName.value.trim() || `Room ${index + 1}`;
  }
  renamingRoomIndex.value = null;
};

const timeRemainingSeconds = ref(0);
const formattedTimeRemaining = ref('00:00');
let timerInterval = null;

const updateTimer = () => {
  if (!sessionStore.breakoutsActive || !sessionStore.breakoutEndTime) {
    timeRemainingSeconds.value = 0;
    formattedTimeRemaining.value = '00:00';
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    return;
  }

  const now = new Date();
  const endTime = new Date(sessionStore.breakoutEndTime);
  const diff = Math.floor((endTime - now) / 1000);

  if (diff <= 0) {
    timeRemainingSeconds.value = 0;
    formattedTimeRemaining.value = '00:00';
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  } else {
    timeRemainingSeconds.value = diff;
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    formattedTimeRemaining.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
};

const startCountdownTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  if (sessionStore.breakoutEndTime) {
    updateTimer();
    timerInterval = setInterval(updateTimer, 1000);
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);

  if (sessionStore.breakoutsActive && sessionStore.breakoutEndTime) {
    startCountdownTimer();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
});

watch(() => sessionStore.breakoutEndTime, (endTime) => {
  if (sessionStore.breakoutsActive && endTime) {
    startCountdownTimer();
  } else {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    formattedTimeRemaining.value = '00:00';
  }
});

watch(() => sessionStore.breakoutsActive, (isActive) => {
  if (!isActive && timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
});

const shuffleParticipants = (options = {}) => {
  // If options is an event (from @click if not using params), includeMain will be undefined
  const includeMain = options.includeMain === true;

  // Collect participants
  const allParticipants = includeMain ? [...mainList.value] : [];
  if (includeMain) {
    mainList.value = [];
  }

  const newRooms = rooms.value.map(r => {
    const participantsInRoom = [...r.participants];
    allParticipants.push(...participantsInRoom);
    return { ...r, participants: [] };
  });

  // Shuffle using Fisher-Yates algorithm
  for (let i = allParticipants.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allParticipants[i], allParticipants[j]] = [allParticipants[j], allParticipants[i]];
  }

  // Distribute evenly across rooms
  const roomCount = newRooms.length;
  if (roomCount === 0) {
    if (includeMain) {
       mainList.value = allParticipants;
    }
    return;
  }

  allParticipants.forEach((participant, index) => {
    const roomIndex = index % roomCount;
    newRooms[roomIndex].participants.push(participant);
  });

  // Assign new array map to force Vue 3 reactivity
  rooms.value = newRooms;
};

const clearAssignments = () => {
  rooms.value.forEach(r => {
    mainList.value.push(...r.participants);
    r.participants = [];
  });
};

const removeParticipant = (roomIdx, pIdx) => {
  const [removed] = rooms.value[roomIdx].participants.splice(pIdx, 1);
  mainList.value.push(removed);
};

const removeParticipantMain = (pIdx) => {
  mainList.value.splice(pIdx, 1);
};

const getParticipantName = (identity) => {
  return sessionStore.getParticipantNameFallback(identity);
};

const joinRoom = (roomName) => {
  sessionStore.requestMoveToBreakout(sessionStore.localParticipantIdentity, 'Admin', roomName);
};

const submitBroadcast = () => {
  if (!broadcastText.value.trim()) return;
  sessionStore.requestBroadcastMessage(broadcastText.value);
  broadcastText.value = '';
  showBroadcastModal.value = false;
};

const openEndBreakoutModal = () => {
  showEndBreakoutModal.value = true;
};

const confirmEndBreakouts = () => {
  const countdown = settings.showCountdown ? settings.closingCountdown : 0;
  if (countdown > 0) {
    sessionStore.requestSendClosingCountdown(countdown);
    // Don't end immediately — the countdown handler on the admin side will
    // trigger endAllBreakouts when the countdown reaches 0.
  } else {
    sessionStore.requestEndBreakouts(0);
  }
  showEndBreakoutModal.value = false;
};

const startBreakouts = async () => {
  if (isStarting.value) return;

  // Validate: at least one room must have participants
  const hasAssignedParticipants = rooms.value.some(r => r.participants.length > 0);
  if (!hasAssignedParticipants) {
    console.warn('No participants assigned to any breakout room');
    // return;
  }

  isStarting.value = true;

  try {
    const mainRoom = sessionStore.roomName;

    // Build breakout room configs
    const breakoutConfig = [];

    for (let i = 0; i < rooms.value.length; i++) {
      const room = rooms.value[i];
      // Create all rooms even if empty, so admin can join any room later

      const breakoutRoomName = nextBreakoutRoom(mainRoom);

      // Create the breakout room through the hub, which opens it only under
      // this session's room (XN-4823)
      await axios.post(`/api/live/rooms`, {
        name: breakoutRoomName,
        emptyTimeout: 300, // 5 min empty timeout
        maxParticipants: 100
      });

      breakoutConfig.push({
        roomName: breakoutRoomName,
        displayName: room.name || `Room ${i + 1}`,
        participantIdentities: room.participants.map(p => p.identity)
      });
    }

    // Store breakout room configs in session store
    const startTime = new Date().toISOString();
    const duration = settings.setTimeLimit ? settings.breakoutDuration : null;
    const endTime = duration ? new Date(Date.now() + duration * 60000).toISOString() : null;

    sessionStore.setBreakoutRooms(breakoutConfig);
    sessionStore.setBreakoutsActive(true);
    // Set start time on store so the admin's elapsed timer works immediately
    sessionStore.setBreakoutConfig({
      showCountdown: settings.showCountdown,
      closingCountdown: settings.closingCountdown,
      allowLeaveEarly: settings.allowLeaveEarly,
      attendeesChoose: settings.attendeesChoose,
      endTime,
      startTime
    });

    // Push startBreakout action for Session.vue to broadcast to participants
    sessionStore.requestStartBreakouts({
      rooms: breakoutConfig,
      mainRoomName: mainRoom,
      attendeesChoose: settings.attendeesChoose,
      duration,
      endTime,
      showCountdown: settings.showCountdown,
      closingCountdown: settings.closingCountdown,
      allowLeaveEarly: settings.allowLeaveEarly,
      startTime: startTime
    });

    console.log('Breakout rooms created and broadcast initiated:', breakoutConfig);

    // Reset state and go back to start view (which will show "Breakouts Active")
    isStarting.value = false;
    view.value = 'start';
  } catch (e) {
    console.error('Failed to start breakout rooms:', e);
    isStarting.value = false;
  }
};

const endBreakouts = () => {
  sessionStore.requestEndBreakouts();
};

// ----- A phone (prototype sheetRooms, host) -----
// Rooms, Automatically or Let them choose, and a time limit with a 60-second
// warning: the same start as the panel's, with those answers.
const phoneRooms = computed(() => sessionStore.phoneRoom);
const phoneMode = ref('auto'); // 'auto' | 'choose'
const phoneMinutes = ref(15);
const PHONE_WARNING = 60;
// The room's one breakout clock (Session.vue, helpers/breakoutPhone).
const phoneSeconds = computed(() => sessionStore.breakoutClock?.seconds ?? null);
const phoneClosing = computed(() => Boolean(sessionStore.breakoutClock?.closing));
const stepRooms = (by) => { roomsCount.value = Math.min(MAX_ROOMS, Math.max(1, roomsCount.value + by)); };
const stepMinutes = (by) => { phoneMinutes.value = Math.min(60, Math.max(5, phoneMinutes.value + by)); };
const initialsOf = (name) => String(name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const openPhoneRooms = async () => {
  if (isStarting.value) return;
  Object.assign(settings, {
    setTimeLimit: true,
    breakoutDuration: phoneMinutes.value,
    showCountdown: true,
    closingCountdown: PHONE_WARNING,
    allowLeaveEarly: true,
    attendeesChoose: phoneMode.value === 'choose',
  });
  isAutomatic.value = true;
  initializeSetup();
  await startBreakouts();
  if (sessionStore.breakoutsActive) {
    sessionStore.setPhoneConfirm({ name: `${roomsCount.value} rooms are open`, text: 'Everyone has been invited', icon: 'rooms', at: Date.now() });
  }
};

// Close all rooms: the 60-second warning, then everybody is back.
const closeAllPhone = () => {
  sessionStore.requestSendClosingCountdown(PHONE_WARNING);
  sessionStore.setPhoneConfirm({ name: 'Rooms close in 60 seconds', text: 'Everyone returns to the main session', icon: 'clock', at: Date.now() });
};

const toggleRoomMenu = (index, event) => {
  event.stopPropagation();
  if (activeMenuRoomIndex.value === index) {
    activeMenuRoomIndex.value = null;
  } else {
    activeMenuRoomIndex.value = index;
  }
};

const confirmClearRoom = (index) => {
  roomToClear.value = index;
  showClearModal.value = true;
  activeMenuRoomIndex.value = null;
};

const executeClearRoom = () => {
  if (roomToClear.value !== null) {
    const index = roomToClear.value;
    if (index === -1) {
      mainList.value = [];
    } else {
      mainList.value.push(...rooms.value[index].participants);
      rooms.value[index].participants = [];
    }
    showClearModal.value = false;
    roomToClear.value = null;
  }
};

const closeClearModal = () => {
  showClearModal.value = false;
  roomToClear.value = null;
};

const confirmDeleteRoom = (index) => {
  roomToDelete.value = index;
  showDeleteModal.value = true;
  activeMenuRoomIndex.value = null;
};

const executeDeleteRoom = () => {
  if (roomToDelete.value !== null) {
    const index = roomToDelete.value;
    mainList.value.push(...rooms.value[index].participants);
    rooms.value.splice(index, 1);
    roomsCount.value = rooms.value.length;
    showDeleteModal.value = false;
    roomToDelete.value = null;
  }
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  roomToDelete.value = null;
};

const addRoom = () => {
  if (rooms.value.length >= MAX_ROOMS) return;
  roomsCount.value++;
  rooms.value.push({
    id: Date.now(),
    expanded: true,
    participants: []
  });
};

// ==================== EDIT MODE ====================

/**
 * Initialize edit mode: populate setup view from the current active breakout state.
 */
const initializeEditMode = () => {
  isEditMode.value = true;
  view.value = 'setup';

  // Populate settings from sessionStore
  if (sessionStore.breakoutEndTime && sessionStore.breakoutStartTime) {
    settings.setTimeLimit = true;
    const startTime = new Date(sessionStore.breakoutStartTime);
    const endTime = new Date(sessionStore.breakoutEndTime);
    // Use total duration to keep input stable during edits
    const totalDuration = Math.round((endTime - startTime) / 60000);
    settings.breakoutDuration = totalDuration;
    originalBreakoutDuration.value = totalDuration;
  } else {
    originalBreakoutDuration.value = null;
  }
  settings.showCountdown = sessionStore.breakoutShowCountdown;
  settings.closingCountdown = sessionStore.breakoutClosingCountdown || 30;
  settings.allowLeaveEarly = sessionStore.breakoutAllowLeaveEarly;
  settings.attendeesChoose = sessionStore.breakoutAttendeesChoose;

  // Build rooms from current breakout state
  const currentBreakoutRooms = sessionStore.breakoutRooms;
  const breakoutIdentities = new Set(
    currentBreakoutRooms.flatMap(r => r.participantIdentities || [])
  );

  // Build participant objects from session participants
  const allParticipants = sessionParticipantsList.value;

  // Populate breakout rooms with participant objects
  rooms.value = currentBreakoutRooms.map((br, index) => ({
    id: index + 1,
    name: br.displayName || br.roomName,
    roomName: br.roomName, // Keep track of original room name
    expanded: true,
    participants: (br.participantIdentities || []).map(identity => {
      const p = allParticipants.find(ap => ap.identity === identity);
      return p || {
        name: sessionStore.getParticipantNameFallback(identity),
        identity: identity,
        avatar: null
      };
    })
  }));

  roomsCount.value = rooms.value.length;

  // Main list: participants not in any breakout room
  mainList.value = allParticipants.filter(p => !breakoutIdentities.has(p.identity));

  // Switch to manual mode for editing
  isAutomatic.value = false;

  mainRoomExpanded.value = true;
};

/**
 * Cancel edit mode: go back to active view without saving.
 */
const cancelEdit = () => {
  isEditMode.value = false;
  view.value = 'start';
};

/**
 * Save breakout edits: compute diff from current state and broadcast changes.
 */
const saveBreakoutEdits = async () => {
  if (isSaving.value) return;
  isSaving.value = true;

  try {
    const mainRoom = sessionStore.roomName;

    // Build updated breakout config
    const updatedConfig = [];
    for (let i = 0; i < rooms.value.length; i++) {
      const room = rooms.value[i];
      const breakoutRoomName = room.roomName || nextBreakoutRoom(mainRoom);

      // Create room if it's new (no existing roomName from store)
      if (!room.roomName) {
        try {
          await axios.post(`/api/live/rooms`, {
            name: breakoutRoomName,
            emptyTimeout: 300,
            maxParticipants: 100
          });
        } catch (e) {
          // The hub refusing the name (403/404) means nobody may be sent
          // there, so it is left out. Anything else is LiveKit's: the room
          // is created when its first person joins.
          if ([403, 404].includes(e.response?.status)) {
            console.warn('Breakout refused by the hub, left out:', breakoutRoomName, e);
            continue;
          }
          console.warn('Room creation failed (may already exist):', breakoutRoomName, e);
        }
      }

      updatedConfig.push({
        roomName: breakoutRoomName,
        displayName: room.name || `Room ${i + 1}`,
        participantIdentities: room.participants.map(p => p.identity)
      });
    }

    // Compute the diff: determine who needs to move
    const oldRooms = sessionStore.breakoutRooms;
    const oldAssignments = {}; // identity -> roomName
    oldRooms.forEach(r => {
      (r.participantIdentities || []).forEach(id => {
        oldAssignments[id] = r.roomName;
      });
    });

    const newAssignments = {}; // identity -> roomName
    updatedConfig.forEach(r => {
      r.participantIdentities.forEach(id => {
        newAssignments[id] = r.roomName;
      });
    });

    // Participants who moved to a different room
    const movedParticipants = [];
    // Participants moved from breakout back to main
    const returnToMain = [];
    // Participants newly assigned to a breakout
    const newlyAssigned = [];

    // Check all new assignments
    for (const [identity, newRoom] of Object.entries(newAssignments)) {
      const oldRoom = oldAssignments[identity];
      if (!oldRoom) {
        // Was in main, now assigned to a breakout room
        newlyAssigned.push({ identity, targetRoom: newRoom });
      } else if (oldRoom !== newRoom) {
        // Was in a different breakout room
        movedParticipants.push({ identity, targetRoom: newRoom });
      }
    }

    // Check for participants removed from breakout rooms (back to main)
    for (const [identity, oldRoom] of Object.entries(oldAssignments)) {
      if (!newAssignments[identity]) {
        returnToMain.push({ identity, fromRoom: oldRoom });
      }
    }

    // Update the session store with new room config
    sessionStore.setBreakoutRooms(updatedConfig);

    // Update settings in session store
    let newEndTime = sessionStore.breakoutEndTime;
    if (settings.setTimeLimit) {
      if (settings.breakoutDuration !== originalBreakoutDuration.value || !newEndTime) {
        // Explicit duration change: calculate new end time relative to start time (or now if starting fresh)
        const baseTime = (sessionStore.breakoutStartTime && newEndTime)
            ? new Date(sessionStore.breakoutStartTime)
            : new Date();
        newEndTime = new Date(baseTime.getTime() + settings.breakoutDuration * 60000).toISOString();
      }
    } else {
      newEndTime = null;
    }

    sessionStore.setBreakoutConfig({
      showCountdown: settings.showCountdown,
      closingCountdown: settings.closingCountdown,
      allowLeaveEarly: settings.allowLeaveEarly,
      attendeesChoose: settings.attendeesChoose,
      endTime: newEndTime,
      startTime: sessionStore.breakoutStartTime || (settings.setTimeLimit ? new Date().toISOString() : null)
    });

    // Push updateBreakout action for Session.vue to process
    sessionStore.requestUpdateBreakouts({
      rooms: updatedConfig,
      mainRoomName: mainRoom,
      movedParticipants,
      returnToMain,
      newlyAssigned,
      attendeesChoose: settings.attendeesChoose,
      duration: settings.setTimeLimit ? settings.breakoutDuration : null,
      endTime: newEndTime,
      showCountdown: settings.showCountdown,
      closingCountdown: settings.closingCountdown,
      allowLeaveEarly: settings.allowLeaveEarly,
      startTime: sessionStore.breakoutStartTime || (settings.setTimeLimit ? new Date().toISOString() : null)
    });

    isEditMode.value = false;
    isSaving.value = false;
    view.value = 'start';
  } catch (e) {
    console.error('Failed to save breakout edits:', e);
    isSaving.value = false;
  }
};
</script>

<style scoped>
/* A phone: the prototype's .setrow, .stepper, .seg, .inroom and .room-card. */
.bo-phone { font-family: var(--xl-font-family); color: #1C1720; }
.bo-phone__note { margin: 0 2px 10px; color: #867D8D; font-size: 12.5px; line-height: 1.5; }
.bo-phone__row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 0; border-bottom: 1px solid #F1ECF4; font-size: 14px; }
.bo-phone__row--block { display: block; }
.bo-phone__row--block > div:first-child { margin-bottom: 8px; }
.bo-phone__row span { display: block; margin-top: 1px; color: #867D8D; font-size: 12px; }
.bo-phone__step { display: flex; align-items: center; overflow: hidden; border: 1px solid #E2DBE6; border-radius: 11px; }
.bo-phone__step button { width: 38px; height: 38px; border: 0; background: none; color: #514A57; font-size: 18px; cursor: pointer; }
.bo-phone__step button:disabled { opacity: 0.35; cursor: default; }
.bo-phone__step b { min-width: 34px; text-align: center; font-variant-numeric: tabular-nums; }
.bo-phone__seg { display: flex; padding: 3px; border-radius: 12px; background: #F3EFF5; }
.bo-phone__seg button { flex: 1; height: 34px; border: 0; border-radius: 9px; background: none; color: #514A57; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.bo-phone__seg button.on { background: #fff; color: #1C1720; box-shadow: 0 1px 3px rgba(28, 23, 32, 0.12); }
.bo-phone__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 44px;
  margin-top: 14px;
  border: 0;
  border-radius: 99px;
  background: #5E2F78;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.bo-phone__btn--g { margin-top: 0; border: 1px solid #E2DBE6; background: #fff; color: #1C1720; }
.bo-phone__btn--red { background: #B42A22; }
.bo-phone__btn:disabled { opacity: 0.6; cursor: progress; }
.bo-phone button:focus-visible { outline: 2px solid #5E2F78; outline-offset: 2px; }
.bo-phone__live { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; padding: 14px; border: 1px solid #E7DCEF; border-radius: 16px; background: linear-gradient(135deg, #F5EFF9, #FBF8FD); }
.bo-phone__live b { font-size: 15px; }
.bo-phone__live p { margin: 0 0 8px; color: #514A57; font-size: 13px; }
.bo-phone__clock { font-variant-numeric: tabular-nums; }
.bo-phone__card { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; padding: 12px; border: 1px solid #EDE7F0; border-radius: 14px; }
.bo-phone__t { flex: 1; min-width: 0; }
.bo-phone__t b { display: block; font-size: 14px; }
.bo-phone__t > span { display: block; color: #867D8D; font-size: 12px; }
.bo-phone__avs { display: flex; margin-top: 6px; }
.bo-phone__avs span { display: grid; place-items: center; width: 26px; height: 26px; margin-left: -6px; border: 2px solid #fff; border-radius: 50%; background: #EBDDF3; color: #5E2F78; font-size: 10px; font-weight: 700; }
.bo-phone__avs span:first-child { margin-left: 0; }
.bo-phone__join { flex: 0 0 auto; height: 32px; padding: 0 14px; border: 1px solid #E2DBE6; border-radius: 99px; background: #fff; color: #1C1720; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.bo-phone__join:disabled { opacity: 0.55; cursor: default; }
</style>

<style scoped lang="scss">
/* Breakout rooms in the session panel, in the prototype's language (boPane:
   .botop, .boseg, .boset, .bocard, .bochip, .rmempty, .rmsend; the broadcast
   dialog is the room overlay .mod / .mdl.ses). Tokens come from the panel's
   [data-xl-gen="northstar"] root; the hex after each is the prototype's. */
$pur: var(--xl-color-primary, #5E2F78);
$pur-2: var(--xl-color-surface-tint, #8154A6);
$tint: var(--xl-color-primary-container, #F5EFF9);
$tint-2: #EADFF3;
$tint-3: #D9C6E8;
$line: var(--xl-color-border, #EDE7F0);
$line-2: var(--xl-color-border-subtle, #F6F2F8);
$ink: var(--xl-color-on-surface, #1C1720);
$ink-2: var(--xl-color-on-surface-variant, #514A57);
$mut: var(--xl-color-outline, #867D8D);
$faint: var(--xl-color-outline-variant, #B6AEBC);
$cr: var(--xl-color-error, #A32020);
$cr-bg: var(--xl-color-error-container, #FCEBEB);
$wa: var(--xl-color-warning, #8A5200);
$paper: var(--xl-color-surface, #FCFAFD);
$sh: 0 1px 2px rgba(28, 23, 32, 0.04);

.breakout-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  position: relative;
  font-family: var(--xl-font-family);
  color: $ink;
}

.breakout-widget button:not(.xl-btn) {
  font-family: inherit;
}

.breakout-widget button:focus-visible,
.breakout-widget input:focus-visible {
  outline: 2px solid $pur;
  outline-offset: 2px;
}

/* ---------- header: a slim bar under the panel's own header ---------- */
.widget-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid $line-2;
}


/* .rmhide */
.icon-btn {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  border: 0;
  padding: 0;
  background: none;
  color: $mut;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: $line-2;
    color: $ink;
  }
}

.widget-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
}

/* ---------- empty and ended (.rmempty) ---------- */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 30px 24px;
  gap: 4px;

  .xl-btn {
    margin-top: 8px;
  }
}

.empty-icon-wrapper {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: $line-2;
  color: $mut;
  display: grid;
  place-items: center;
  margin-bottom: 8px;
}

.empty-title {
  margin: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: $ink;
}

.empty-desc {
  font-size: 12.5px;
  color: $mut;
  margin: 0 0 4px;
  line-height: 1.55;
  max-width: 230px;
}

/* ---------- running ---------- */
.active-breakouts-container {
  display: flex;
  flex-direction: column;
}

.active-stats-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 11px 14px 10px;
  border-bottom: 1px solid $line-2;
}

.stats-left .stat-room-name {
  font-size: 13.5px;
  font-weight: 600;
  color: $ink;
}

.stats-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: $mut;
  font-variant-numeric: tabular-nums;
}

.stat-divider {
  width: 1px;
  height: 14px;
  background-color: $line;
}

/* .tag.cr */
.stat-live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 23px;
  padding: 0 9px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: $cr-bg;
  color: $cr;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $cr;
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% { box-shadow: 0 0 0 0 rgba(163, 32, 32, 0.4); }
  70% { box-shadow: 0 0 0 5px rgba(163, 32, 32, 0); }
  100% { box-shadow: 0 0 0 0 rgba(163, 32, 32, 0); }
}

/* the countdown pill (.rmsay.bo .boleft) */
.stat-time {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: $wa;
  background: #fff;
  border: 1px solid $wa;
  border-radius: 99px;
  padding: 2px 9px;
  font-size: 12px;
}

.active-action-row {
  display: flex;
  gap: 8px;
  padding: 12px 14px 0;

  .xl-btn {
    flex: 1;
    min-width: 0;
  }
}

/* ---------- setting up: .botop ---------- */
.setup-container {
  display: flex;
  flex-direction: column;
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 14px 10px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;

  label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    color: $ink-2;
  }
}

/* .stepper */
.number-input-wrapper {
  display: inline-flex;
  align-items: center;
  border: 1px solid $line;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;

  &.focused {
    border-color: $pur;
    box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
  }
}

.spin-btn {
  width: 26px;
  height: 30px;
  border: 0;
  padding: 0;
  background: none;
  font-size: 15px;
  color: $mut;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: $line-2;
    color: $ink;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.rooms-input {
  width: 22px;
  min-width: 22px;
  height: 30px;
  border: 0;
  padding: 0;
  background: transparent;
  text-align: center;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: $ink;
  -moz-appearance: textfield;
  appearance: textfield;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.control-actions {
  flex: 1 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;

  > :last-child {
    margin-left: auto;
  }
}

.lnk {
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: $pur;
  padding: 6px 9px;
  border-radius: 9px;
  white-space: nowrap;

  &:hover {
    background: $tint;
    color: $pur;
  }
}

/* ---------- .boseg and .boset ---------- */
.mode-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 14px 12px;
}

.mode-row .mode-toggle {
  flex: 1;
  min-width: 0;
  display: flex;
}

.seg {
  flex: 1;
  display: flex;
  gap: 2px;
  padding: 3px;
  background: $line-2;
  border-radius: 10px;

  button {
    flex: 1;
    height: 26px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    color: $mut;
    border: none;
    background: none;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;

    &.on {
      background: #fff;
      color: $pur;
      box-shadow: $sh;
    }
  }
}

/* The gear and its panel are laid out by .mode-row; the wrapper stays in the
   DOM only to tell a click inside the settings from one outside them. */
.settings-wrapper {
  display: contents;
}

.ib {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: 1px solid $line;
  color: $ink-2;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: $paper;
  }

  &.on {
    background: $tint;
    color: $pur;
  }
}

.settings-dropdown {
  flex: 0 0 100%;
  margin-top: 4px;
  padding: 2px 14px;
  background: $paper;
  border: 1px solid $line;
  border-radius: 14px;
}

.settings-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid $line-2;

  h4 {
    margin: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: $ink;
  }
}

.settings-header-text {
  flex: 1;
  min-width: 0;
}

.settings-subtitle {
  font-size: 11.5px;
  line-height: 1.5;
  color: $mut;
}

.close-settings {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  margin: -4px -6px 0 0;
  border: 0;
  padding: 0;
  border-radius: 9px;
  background: none;
  color: $mut;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: $line-2;
    color: $ink;
  }
}

.settings-body {
  display: flex;
  flex-direction: column;
}

/* .boset .row */
.setting-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid $line-2;

  &:last-child {
    border-bottom: none;
  }

  .xl-switch {
    width: 100%;
    gap: 14px;
    justify-content: space-between;
  }

  :deep(.xl-switch__text) {
    flex: 1;
    min-width: 0;
  }

  :deep(.xl-switch__text b) {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.35;
    color: $ink;
  }
}

.setting-label {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: $ink;
}

.select-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

/* .inp at the settings size */
.config-input {
  width: 56px;
  height: 30px;
  border: 1px solid $line;
  border-radius: 11px;
  padding: 0 8px;
  background: #fff;
  font: inherit;
  font-size: 12.5px;
  text-align: center;
  color: $ink;
  -moz-appearance: textfield;
  appearance: textfield;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: $pur;
    box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
  }

  &:disabled {
    background: $line-2;
    border-color: $line-2;
    color: $mut;
    cursor: not-allowed;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.input-suffix {
  font-size: 12.5px;
  color: $mut;
}

/* .boset .row.dim */
.setting-item.select-item.disabled {
  opacity: 0.45;
}

/* ---------- room cards (.borooms-list / .bocard) ---------- */
.rooms-list {
  padding: 10px 14px;
}

.room-card {
  border: 1px solid $line;
  border-radius: 14px;
  background: #fff;
  margin-bottom: 10px;
  box-shadow: 0 1px 2px rgba(28, 23, 32, 0.03);
  transition: 0.14s;

  &.expanded {
    box-shadow: 0 2px 8px rgba(28, 23, 32, 0.06);
  }
}

/* .bohead */
.room-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 8px;
  cursor: pointer;
}

.room-header-left {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* .tw */
.toggle-icon {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  color: $mut;
  display: grid;
  place-items: center;
  transition: 0.15s;
}

.room-header:hover .toggle-icon {
  background: $line-2;
  color: $ink;
}

.room-card.expanded .toggle-icon {
  transform: rotate(180deg);
}

.room-name {
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* .bohead .n */
.room-count {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: $mut;
  background: $line-2;
  padding: 1px 8px;
  border-radius: 99px;
  font-variant-numeric: tabular-nums;
}

.room-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

/* .bocard .btn.sm */
.room-card .room-header .room-join {
  height: 30px;
  border-radius: 99px;
  padding: 0 15px;
  font-size: 12.5px;
}

.current-room-badge {
  white-space: nowrap;
}

.room-menu-wrapper {
  position: relative;
  flex: 0 0 auto;
}

/* .bohead .ib */
.icon-btn.dots-btn {
  border: 1px solid $line;
  border-radius: 8px;
  font-size: 15px;
  letter-spacing: 1px;
  line-height: 1;
}

/* .bomenu */
.menu-dropdown {
  position: absolute;
  top: 30px;
  right: 0;
  z-index: 40;
  width: 188px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 11px;
  box-shadow: 0 12px 30px rgba(28, 23, 32, 0.16);
  padding: 5px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 9px;
  border: 0;
  border-radius: 8px;
  background: none;
  font-size: 12.5px;
  color: $ink-2;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: $line-2;
  }

  &.delete {
    color: $cr;

    &:hover {
      background: $cr-bg;
    }
  }
}

.menu-icon {
  display: grid;
  place-items: center;
  color: inherit;
}

.rename-input-wrapper {
  flex: 1;
  min-width: 0;
}

.rename-input {
  width: 100%;
  height: 28px;
  border: 1px solid $line;
  border-radius: 8px;
  padding: 0 8px;
  background: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: $ink;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: $pur;
    box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
  }
}

/* .bobody */
.room-content {
  padding: 0 12px 12px;
}

.participants-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

/* .boempty */
.room-empty-state {
  font-size: 12.5px;
  color: $faint;
  margin: 0;
  padding: 4px 2px 6px;
  font-style: italic;
}

/* .bochip */
.item-container__selected__item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  padding: 3px 5px 3px 3px;
  border-radius: 99px;
  border: 1px solid $line;
  background: $paper;
  font-size: 12.5px;
  user-select: none;
  cursor: grab;

  &__img {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    overflow: hidden;
    display: grid;
    place-items: center;
    background: $tint-2;

    img,
    svg {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__title {
    min-width: 0;
    font-weight: 500;
    color: $ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* .bochip .x */
  .icon-close {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    border: 0;
    padding: 0;
    border-radius: 50%;
    background: none;
    color: $faint;
    display: grid;
    place-items: center;
    cursor: pointer;

    &:hover {
      background: $cr-bg;
      color: $cr;
    }
  }

  /* a chip with nothing to remove keeps an even inset on the right */
  &.is-static {
    padding-right: 10px;
  }
}

.ghost-card {
  opacity: 0.5;
  background: $tint;
  border: 1px dashed $tint-3;
}

/* .boadd */
.rooms-list .add-room.xl-btn {
  width: 100%;
  border-style: dashed;
  height: 38px;
  border-radius: 11px;
  font-size: 13px;
}

/* ---------- footer (.rmsend) ---------- */
.breakout-footer {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 14px;
  border-top: 1px solid $line;
  background: #fff;
}

.footer-actions {
  display: flex;
  gap: 8px;

  .xl-btn {
    flex: 1;
    min-width: 0;
  }
}

.validation-error {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: $cr;
}

/* ---------- broadcast (.mod / .mdl.ses) ---------- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(28, 23, 32, 0.44);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
}

.modal-card {
  background: #fff;
  border-radius: 22px;
  width: 100%;
  max-width: 440px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 80px rgba(28, 23, 32, 0.3);
  overflow: hidden;
  font-family: var(--xl-font-family);
  color: $ink;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: $ink;
  }
}

.modal-head {
  padding: 24px 26px 14px;
  border-bottom: 1px solid $line-2;
}

/* .sesmark */
.modal-mark {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: $tint;
  color: $pur;
  margin-bottom: 14px;
}

.modal-body {
  padding: 20px 26px;
  overflow-y: auto;
  flex: 1;

  /* .inp, multi-line */
  textarea {
    display: block;
    width: 100%;
    border: 1px solid $line;
    border-radius: 11px;
    padding: 10px 13px;
    background: #fff;
    font: inherit;
    font-size: 14.5px;
    line-height: 1.5;
    color: $ink;
    resize: none;

    &:focus,
    &:focus-visible {
      outline: none;
      border-color: $pur;
      box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.char-count {
  font-size: 11.5px;
  color: $faint;
  font-variant-numeric: tabular-nums;
}

/* .mdl.ses .mf */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 16px 26px;
  border-top: 1px solid $line-2;
  background: $paper;

  .xl-btn {
    min-width: 104px;
    white-space: nowrap;
  }
}

@media (prefers-reduced-motion: reduce) {
  .live-dot {
    animation: none;
  }

  .room-card,
  .toggle-icon,
  .icon-btn,
  .seg button,
  .ib,
  .number-input-wrapper {
    transition: none;
  }
}
</style>
